import { addDays, format, isSameDay, startOfWeek } from 'date-fns';

const WEEK_OPTS = { weekStartsOn: 0 as const };

/** Sunday of the week containing `date`, formatted as YYYY-MM-DD. */
export function toWeekStartIso(date: Date): string {
  const weekStart = startOfWeek(date, WEEK_OPTS);
  return format(weekStart, 'yyyy-MM-dd');
}

export function weekRangeForAnchor(weekAnchor: Date): { start: Date; end: Date } {
  const start = startOfWeek(weekAnchor, WEEK_OPTS);
  return { start, end: addDays(start, 6) };
}

export function isDayInWeek(day: Date, weekAnchor: Date): boolean {
  const { start, end } = weekRangeForAnchor(weekAnchor);
  const dayTime = new Date(day.getFullYear(), day.getMonth(), day.getDate()).getTime();
  return dayTime >= start.getTime() && dayTime <= end.getTime();
}

export function isWeekRangeStart(day: Date, weekAnchor: Date): boolean {
  return isSameDay(day, weekRangeForAnchor(weekAnchor).start);
}

export function isWeekRangeEnd(day: Date, weekAnchor: Date): boolean {
  return isSameDay(day, weekRangeForAnchor(weekAnchor).end);
}

export function isWeekRangeMiddle(day: Date, weekAnchor: Date): boolean {
  return isDayInWeek(day, weekAnchor) && !isWeekRangeStart(day, weekAnchor) && !isWeekRangeEnd(day, weekAnchor);
}

export function weekStartIsoToDate(weekStart: string): Date | null {
  const [y, m, d] = weekStart.split('-').map(Number);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
  const date = new Date(y, m - 1, d);
  if (Number.isNaN(date.getTime())) return null;
  return date;
}

/** e.g. "19 May – 25 May 2026" */
export function formatWeekLabel(weekStart: string): string {
  const start = weekStartIsoToDate(weekStart);
  if (!start) return weekStart;
  const end = addDays(start, 6);
  const sameYear = start.getFullYear() === end.getFullYear();
  if (sameYear) {
    return `${format(start, 'd MMM')} – ${format(end, 'd MMM yyyy')}`;
  }
  return `${format(start, 'd MMM yyyy')} – ${format(end, 'd MMM yyyy')}`;
}

export function defaultWeekStartIso(): string {
  return toWeekStartIso(new Date());
}
